import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { MapPin, Plus, Send, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, PageHeader, Skeleton, Spinner, useToast } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import { useSession } from '../../lib/session';
import { MapView } from '../../map/MapView';
import { ClickCapture, MarkersLayer, type PointMarker } from '../../map/MarkersLayer';
import { PlacesLayer, type PlaceGeom } from '../../map/PlacesLayer';
import type { Customer } from '../customers/CustomersPage';
import type { DriverRow } from '../drivers/DriversPage';
import type { VehicleRow } from '../vehicles/VehiclesPage';
import type { TripDetail } from './types';

interface Point {
  placeId: string;
  text: string;
  lat: number | null;
  lon: number | null;
}
interface StopDraft extends Point {
  key: string;
  plannedAt: string;
}
interface FormState {
  vehicleId: string;
  driverId: string;
  customerId: string;
  shipmentOrderNo: string;
  cargoType: string;
  cargoNotes: string;
  scheduledAt: string;
  dueAt: string;
  notes: string;
  origin: Point;
  dest: Point;
  stops: StopDraft[];
}

const emptyPoint = (): Point => ({ placeId: '', text: '', lat: null, lon: null });
const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '');
const toIso = (local: string) => (local ? new Date(local).toISOString() : null);
const pointDto = (p: Point) => (p.placeId || p.text || p.lat != null ? { placeId: p.placeId || null, text: p.text || null, lat: p.lat, lon: p.lon } : undefined);

type PickTarget = 'origin' | 'dest' | `stop:${string}` | null;

interface PointFieldsProps {
  target: Exclude<PickTarget, null>;
  point: Point;
  label: string;
  places: PlaceGeom[];
  picking: boolean;
  onPick: () => void;
  onPlace: (placeId: string) => void;
  onText: (text: string) => void;
}

/** Module-level (not nested) so inputs keep focus across parent re-renders. */
function PointFields({ target, point, label, places, picking, onPick, onPlace, onText }: PointFieldsProps) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]" data-target={target}>
      <Field label={label}>
        {(fid) => (
          <select id={fid} className="input" value={point.placeId} onChange={(e) => onPlace(e.target.value)}>
            <option value="">{t('trips.freePoint')}</option>
            {places.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label={t('trips.pointText')}>{(fid) => <input id={fid} className="input" value={point.text} onChange={(e) => onText(e.target.value)} placeholder={t('trips.pointTextHint')} />}</Field>
      <div className="flex items-end">
        <button type="button" className={clsx('btn-secondary min-h-10', picking && '!bg-brand-soft !text-brand-ink')} onClick={onPick} title={t('trips.pickOnMap')}>
          <MapPin size={16} aria-hidden />{' '}
          {point.lat != null ? (
            <span dir="ltr" className="text-xs">
              {point.lat.toFixed(4)}, {point.lon?.toFixed(4)}
            </span>
          ) : (
            t('trips.pickOnMap')
          )}
        </button>
      </div>
    </div>
  );
}

/** /trips/new and /trips/:id/edit — same form; points come from a Place or a click on the map. */
export function TripFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const can = useSession((s) => s.can);
  const isEdit = !!id;

  const existing = useQuery({ queryKey: ['trip', id], enabled: isEdit, queryFn: () => api<TripDetail>(`/trips/${id}`) });
  const vehicles = useQuery({ queryKey: ['vehicles-lite'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200&status=ACTIVE').then((r) => r.data) });
  const drivers = useQuery({ queryKey: ['drivers-lite'], queryFn: () => api<Paginated<DriverRow>>('/drivers?pageSize=200&status=ACTIVE').then((r) => r.data) });
  const customers = useQuery({ queryKey: ['customers-lite'], queryFn: () => api<Paginated<Customer>>('/customers?pageSize=200').then((r) => r.data) });
  const places = useQuery({ queryKey: ['places-lite'], queryFn: () => api<Paginated<PlaceGeom>>('/places?pageSize=500').then((r) => r.data) });

  const [f, setF] = useState<FormState>({ vehicleId: '', driverId: '', customerId: '', shipmentOrderNo: '', cargoType: '', cargoNotes: '', scheduledAt: '', dueAt: '', notes: '', origin: emptyPoint(), dest: emptyPoint(), stops: [] });
  const [pick, setPick] = useState<PickTarget>(null);
  const [loaded, setLoaded] = useState(!isEdit);

  useEffect(() => {
    const tr = existing.data;
    if (!tr || loaded) return;
    setF({
      vehicleId: tr.vehicleId ?? '',
      driverId: tr.driverId ?? '',
      customerId: tr.customerId ?? '',
      shipmentOrderNo: tr.shipmentOrderNo ?? '',
      cargoType: tr.cargoType ?? '',
      cargoNotes: tr.cargoNotes ?? '',
      scheduledAt: toLocal(tr.scheduledAt),
      dueAt: toLocal(tr.dueAt),
      notes: tr.notes ?? '',
      origin: { placeId: tr.originPlaceId ?? '', text: tr.originText ?? '', lat: tr.originLat, lon: tr.originLon },
      dest: { placeId: tr.destPlaceId ?? '', text: tr.destText ?? '', lat: tr.destLat, lon: tr.destLon },
      stops: tr.stops.map((s) => ({ key: s.id, placeId: s.placeId ?? '', text: s.addressText ?? '', lat: s.lat, lon: s.lon, plannedAt: toLocal(s.plannedAt) })),
    });
    setLoaded(true);
  }, [existing.data, loaded]);

  // When the driver changes, default the vehicle to the driver's current vehicle.
  const onDriver = (driverId: string) => {
    const d = drivers.data?.find((x) => x.id === driverId);
    setF((p) => ({ ...p, driverId, vehicleId: p.vehicleId || d?.currentVehicle?.id || '' }));
  };

  const setPoint = (target: Exclude<PickTarget, null>, patch: Partial<Point>) =>
    setF((p) => {
      if (target === 'origin') return { ...p, origin: { ...p.origin, ...patch } };
      if (target === 'dest') return { ...p, dest: { ...p.dest, ...patch } };
      const key = target.slice(5);
      return { ...p, stops: p.stops.map((s) => (s.key === key ? { ...s, ...patch } : s)) };
    });

  const choosePlace = (target: Exclude<PickTarget, null>, placeId: string) => {
    const pl = places.data?.find((x) => x.id === placeId);
    setPoint(target, pl ? { placeId, text: pl.name, lat: pl.centerLat, lon: pl.centerLon } : { placeId: '' });
  };

  const onMapClick = useCallback(
    (lat: number, lon: number) => {
      if (!pick) return;
      setPoint(pick, { placeId: '', lat: Math.round(lat * 1e6) / 1e6, lon: Math.round(lon * 1e6) / 1e6 });
      setPick(null);
    },
    [pick],
  );

  const markers = useMemo<PointMarker[]>(() => {
    const out: PointMarker[] = [];
    if (f.origin.lat != null && f.origin.lon != null) out.push({ id: 'o', lat: f.origin.lat, lon: f.origin.lon, label: f.origin.text || t('trips.origin'), glyph: 'A', color: '#0F3D26' });
    f.stops.forEach((s, i) => {
      if (s.lat != null && s.lon != null) out.push({ id: s.key, lat: s.lat, lon: s.lon, label: s.text || `${t('trips.stop')} ${i + 1}`, glyph: String(i + 1), color: '#2563EB' });
    });
    if (f.dest.lat != null && f.dest.lon != null) out.push({ id: 'd', lat: f.dest.lat, lon: f.dest.lon, label: f.dest.text || t('trips.dest'), glyph: 'B', color: '#DB2777' });
    return out;
  }, [f, t]);

  const body = (dispatch: boolean) => ({
    vehicleId: f.vehicleId || null,
    driverId: f.driverId || null,
    customerId: f.customerId || null,
    shipmentOrderNo: f.shipmentOrderNo || null,
    cargoType: f.cargoType || null,
    cargoNotes: f.cargoNotes || null,
    scheduledAt: toIso(f.scheduledAt),
    dueAt: toIso(f.dueAt),
    notes: f.notes || null,
    origin: pointDto(f.origin),
    dest: pointDto(f.dest),
    stops: f.stops.map((s) => ({ placeId: s.placeId || null, addressText: s.text || null, lat: s.lat, lon: s.lon, plannedAt: toIso(s.plannedAt) })),
    ...(isEdit ? {} : { dispatch }),
  });

  const save = useMutation({
    mutationFn: async (dispatch: boolean) => {
      const tr = isEdit ? await api<TripDetail>(`/trips/${id}`, { method: 'PATCH', json: body(false) }) : await api<TripDetail>('/trips', { method: 'POST', json: body(dispatch) });
      if (isEdit && dispatch) return api<TripDetail>(`/trips/${id}/dispatch`, { method: 'POST', json: {} });
      return tr;
    },
    onSuccess: (tr) => {
      toast.success(t('common.saved'));
      void qc.invalidateQueries({ queryKey: ['trips'] });
      void qc.invalidateQueries({ queryKey: ['trip', tr.id] });
      nav(`/trips/${tr.id}`, { replace: true });
    },
    onError: toast.error,
  });

  const canDispatch = can('trips:dispatch') && !!f.vehicleId && !!f.driverId;
  if (isEdit && !loaded) return <Skeleton className="h-96" />;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={isEdit ? `${t('trips.edit')} ${existing.data?.number ?? ''}` : t('trips.new')}
        subtitle={t('trips.formHint')}
        actions={
          <>
            <button className="btn-secondary" onClick={() => nav(-1)}>
              {t('common.cancel')}
            </button>
            <button className="btn-secondary" disabled={save.isPending} onClick={() => save.mutate(false)}>
              {save.isPending ? <Spinner /> : null} {t('common.save')}
            </button>
            {canDispatch ? (
              <button className="btn-primary" disabled={save.isPending} onClick={() => save.mutate(true)}>
                <Send size={16} aria-hidden /> {t('trips.saveAndDispatch')}
              </button>
            ) : null}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <form
          className="card flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate(false);
          }}
          noValidate
        >
          <h2 className="text-sm font-bold text-text-2">{t('trips.assignment')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('trips.driver')}>
              {(fid) => (
                <select id={fid} className="input" value={f.driverId} onChange={(e) => onDriver(e.target.value)}>
                  <option value="">—</option>
                  {drivers.data?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.fullName}
                      {d.currentVehicle ? ` · ${d.currentVehicle.plateAr}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('trips.vehicle')}>
              {(fid) => (
                <select id={fid} className="input" value={f.vehicleId} onChange={(e) => setF((p) => ({ ...p, vehicleId: e.target.value }))}>
                  <option value="">—</option>
                  {vehicles.data?.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.plateAr}
                      {v.name ? ` · ${v.name}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('trips.scheduledAt')}>{(fid) => <input id={fid} type="datetime-local" dir="ltr" className="input" value={f.scheduledAt} onChange={(e) => setF((p) => ({ ...p, scheduledAt: e.target.value }))} />}</Field>
            <Field label={t('trips.dueAt')}>{(fid) => <input id={fid} type="datetime-local" dir="ltr" className="input" value={f.dueAt} onChange={(e) => setF((p) => ({ ...p, dueAt: e.target.value }))} />}</Field>
          </div>

          <h2 className="text-sm font-bold text-text-2">{t('trips.routeSection')}</h2>
          <PointFields target="origin" point={f.origin} label={t('trips.origin')} places={places.data ?? []} picking={pick === 'origin'} onPick={() => setPick(pick === 'origin' ? null : 'origin')} onPlace={(v) => choosePlace('origin', v)} onText={(v) => setPoint('origin', { text: v })} />
          {f.stops.map((s, i) => (
            <div key={s.key} className="rounded-xl border border-border-soft bg-surface-2 p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-bold text-text-muted">
                  {t('trips.stop')} {i + 1}
                </span>
                <button type="button" className="btn-ghost !min-h-8 text-danger" onClick={() => setF((p) => ({ ...p, stops: p.stops.filter((x) => x.key !== s.key) }))} aria-label={t('common.delete')}>
                  <Trash2 size={14} />
                </button>
              </div>
              <PointFields target={`stop:${s.key}`} point={s} label={t('trips.place')} places={places.data ?? []} picking={pick === `stop:${s.key}`} onPick={() => setPick(pick === `stop:${s.key}` ? null : `stop:${s.key}`)} onPlace={(v) => choosePlace(`stop:${s.key}`, v)} onText={(v) => setPoint(`stop:${s.key}`, { text: v })} />
              <Field label={t('trips.plannedAt')}>{(fid) => <input id={fid} type="datetime-local" dir="ltr" className="input max-w-xs" value={s.plannedAt} onChange={(e) => setF((p) => ({ ...p, stops: p.stops.map((x) => (x.key === s.key ? { ...x, plannedAt: e.target.value } : x)) }))} />}</Field>
            </div>
          ))}
          <button type="button" className="btn-secondary self-start" onClick={() => setF((p) => ({ ...p, stops: [...p.stops, { ...emptyPoint(), key: crypto.randomUUID(), plannedAt: '' }] }))}>
            <Plus size={16} /> {t('trips.addStop')}
          </button>
          <PointFields target="dest" point={f.dest} label={t('trips.dest')} places={places.data ?? []} picking={pick === 'dest'} onPick={() => setPick(pick === 'dest' ? null : 'dest')} onPlace={(v) => choosePlace('dest', v)} onText={(v) => setPoint('dest', { text: v })} />

          <h2 className="text-sm font-bold text-text-2">{t('trips.cargoSection')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('trips.customer')}>
              {(fid) => (
                <select id={fid} className="input" value={f.customerId} onChange={(e) => setF((p) => ({ ...p, customerId: e.target.value }))}>
                  <option value="">—</option>
                  {customers.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label={t('trips.shipmentOrderNo')}>{(fid) => <input id={fid} dir="ltr" className="input" value={f.shipmentOrderNo} onChange={(e) => setF((p) => ({ ...p, shipmentOrderNo: e.target.value }))} />}</Field>
            <Field label={t('trips.cargoType')}>{(fid) => <input id={fid} className="input" value={f.cargoType} onChange={(e) => setF((p) => ({ ...p, cargoType: e.target.value }))} placeholder={t('trips.cargoTypeHint')} />}</Field>
            <Field label={t('trips.cargoNotes')}>{(fid) => <input id={fid} className="input" value={f.cargoNotes} onChange={(e) => setF((p) => ({ ...p, cargoNotes: e.target.value }))} />}</Field>
          </div>
          <Field label={t('vehicles.notes')}>{(fid) => <textarea id={fid} className="input min-h-20" value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} />}</Field>
        </form>

        <div className="card relative min-h-[420px] overflow-hidden !p-0 lg:sticky lg:top-4 lg:h-[calc(100dvh-8rem)]">
          {pick ? <div className="absolute inset-x-0 top-0 z-10 bg-brand px-3 py-2 text-center text-sm font-semibold text-white">{t('trips.pickHint', { what: pick === 'origin' ? t('trips.origin') : pick === 'dest' ? t('trips.dest') : t('trips.stop') })}</div> : null}
          <MapView zoom={6} className="h-full">
            <PlacesLayer places={places.data ?? []} />
            <MarkersLayer markers={markers} fit />
            <ClickCapture onClick={onMapClick} active={!!pick} />
          </MapView>
        </div>
      </div>
    </div>
  );
}
